"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_APPROVAL_DURATION_SECONDS = exports.VIDEO_COMPOSITION_ID = void 0;
exports.documentFrameLayout = documentFrameLayout;
exports.documentDurationSeconds = documentDurationSeconds;
exports.documentTransitionWarnings = documentTransitionWarnings;
exports.documentTimingWarnings = documentTimingWarnings;
exports.documentWarnings = documentWarnings;
const caption_layout_1 = require("./caption-layout");
/**
 * The Remotion composition id the render seam selects (server render). Kept next to the frame math
 * so the render entry (packages/video-composition) and the backend render seam agree on one literal.
 */
exports.VIDEO_COMPOSITION_ID = 'VideoDocument';
/** Per-scene frame counts — the rounding every other function here builds on. */
function sceneFrameCounts(doc, fps) {
    return doc.scenes.map((scene) => Math.max(1, Math.round(scene.duration * fps)));
}
/**
 * The transition between scene i and i+1, as it will render — `undefined` for a cut, an untransitioned
 * scene, or the last scene. The overlap is clamped to the shorter of the two scenes it joins: a
 * transition may consume a scene entirely, but it can never push the next scene backwards past the
 * previous one and invert the timeline.
 */
function effectiveTransitions(doc, fps, frames) {
    return doc.scenes.map((scene, i) => {
        const isLast = i === doc.scenes.length - 1;
        const transition = scene.transitionToNext;
        if (isLast || !transition || transition.style === 'cut')
            return undefined;
        const overlap = Math.min(Math.round(transition.duration * fps), frames[i], frames[i + 1]);
        if (overlap <= 0)
            return undefined;
        return { style: transition.style, frames: overlap };
    });
}
/**
 * Frame layout for a document at a given fps. Scene lengths are rounded ONCE here and every start is
 * derived additively from them, so the composition, the Player's `durationInFrames`, the timeline
 * strip, and a server render agree exactly with no ±1-frame gaps.
 *
 * A transition makes its two scenes OVERLAP — the next scene starts `frames` before the previous one
 * ends, and both are mounted through that window (that overlap IS the transition: one scene reveals
 * itself over the other). So the document runs SHORTER than the sum of its scene durations.
 *
 * Pure arithmetic (no React) so the backend render seam / glance frame math can import it WITHOUT
 * pulling in the composition (and thus React).
 */
function documentFrameLayout(doc, fps) {
    const frames = sceneFrameCounts(doc, fps);
    const transitions = effectiveTransitions(doc, fps, frames);
    let acc = 0;
    const sequences = doc.scenes.map((scene, i) => {
        const from = acc;
        acc += frames[i] - (transitions[i]?.frames ?? 0);
        return {
            scene,
            from,
            durationInFrames: frames[i],
            /** Transition INTO this scene: it reveals itself over the previous scene's last frames. */
            enter: i > 0 ? transitions[i - 1] : undefined,
            /** Transition OUT of this scene: the next one reveals itself over these last frames. */
            exit: transitions[i],
        };
    });
    return { sequences, totalFrames: Math.max(1, acc) };
}
/**
 * Total rendered duration in seconds — derived from the frame layout, so it accounts for the overlap
 * transitions introduce and can never disagree with the file that comes out of a render.
 */
function documentDurationSeconds(doc) {
    if (doc.scenes.length === 0)
        return 0;
    return documentFrameLayout(doc, doc.canvas.fps).totalFrames / doc.canvas.fps;
}
/**
 * Output-duration cap (~5 min) — enforced at the approve/render boundary, NOT in the zod schema
 * (which runs on every edit; a long DRAFT must stay editable, only its render is capped).
 *
 * Lives HERE, beside `documentDurationSeconds`, because two surfaces must agree on the same verdict:
 * the approval service rejects an over-cap approve, and the preview's Approve button disables itself
 * before the click. A cap the button couldn't see would mean a button that says "fine" and a service
 * that says "blocked-too-long".
 */
exports.MAX_APPROVAL_DURATION_SECONDS = 300;
/**
 * Deterministic transition lints — where the document asks for something it will not get. Pure
 * arithmetic (no render, no model), returned to the agent on write so it can correct an unrenderable
 * intent while it still has the document in hand.
 *
 * NOT the write-path entry point, despite the name being the most greppable one here: this is ONE
 * lint of several. A write surface calls {@link documentWarnings}, which runs all of them. Exported
 * only so it can be tested in isolation.
 */
function documentTransitionWarnings(doc) {
    const fps = doc.canvas.fps;
    const frames = sceneFrameCounts(doc, fps);
    const warnings = [];
    doc.scenes.forEach((scene, i) => {
        const transition = scene.transitionToNext;
        if (!transition || transition.style === 'cut')
            return;
        if (i === doc.scenes.length - 1) {
            warnings.push(`Scene "${scene.id}" is the last scene, so its ${transition.style} transitionToNext has no effect — there is no next scene to transition into.`);
            return;
        }
        const requested = Math.round(transition.duration * fps);
        if (requested <= 0) {
            warnings.push(`Scene "${scene.id}": the ${transition.style} of ${transition.duration}s is shorter than one frame, so it renders as a hard cut. Give it at least ${(1 / fps).toFixed(2)}s (0.3-0.5s is normal) or set style "cut".`);
            return;
        }
        const allowed = Math.min(frames[i], frames[i + 1]);
        if (requested > allowed) {
            warnings.push(`Scene "${scene.id}": the ${transition.style} of ${transition.duration}s is longer than the shorter of the two scenes it joins, so it renders as ${(allowed / fps).toFixed(2)}s. Shorten it or lengthen the scenes.`);
        }
    });
    return warnings;
}
/**
 * The one window check, used from both frames of reference: `subject` names the layer the way the
 * agent addressed it, `containerSeconds` is the length its window is bounded by, `containerWord` is
 * what to call that container in the sentence.
 */
function layerWindowWarnings(layer, containerSeconds, subject, containerWord) {
    const container = containerSeconds.toFixed(2);
    // A degenerate container has a bigger problem than its layers' windows, and a warning per layer
    // would bury it. Same reasoning as the no-scenes guard on the overlay pass below.
    if (containerSeconds <= 0)
        return [];
    // Only a STATED appearsAt can be wrong. An untimed layer has no window to be warned about, and
    // its implicit 0 would otherwise trip this — telling the agent a layer it never timed "never
    // appears", which is also untrue: the frame layout floors a scene at one frame, so it renders.
    // Worth its own sentence when it IS stated: this layer renders NOTHING. "Clamped" undersells it.
    if (layer.appearsAt != null && layer.appearsAt >= containerSeconds) {
        return [
            `${subject}: appearsAt ${layer.appearsAt}s is at or past the end of its ${containerWord} (${container}s), so it never appears at all. Move it earlier or lengthen the ${containerWord}.`,
        ];
    }
    if (layer.endsAt != null && layer.endsAt > containerSeconds) {
        return [
            `${subject}: endsAt ${layer.endsAt}s is past the end of its ${containerWord} (${container}s), so it renders until ${container}s. Shorten the window or lengthen the ${containerWord}.`,
        ];
    }
    return [];
}
/**
 * Deterministic timing lints — where a layer's `appearsAt`/`endsAt` window asks for time its
 * container does not have. Pure arithmetic, same contract as the transition lints above: the window
 * CLAMPS and the agent hears what it will actually get. (An INVERTED window is the only timing
 * error, and the schema rejects that at write — so nothing here can ever be a rejection.)
 *
 * The two frames of reference the schema defines are the whole subtlety here: a scene layer's
 * container is ITS SCENE (scene-relative seconds); an overlay's container is the whole VIDEO
 * (document-relative seconds).
 *
 * NOT the write-path entry point — a write surface calls {@link documentWarnings}, which runs every
 * lint. Exported only so it can be tested in isolation.
 */
function documentTimingWarnings(doc) {
    const warnings = [];
    for (const scene of doc.scenes) {
        for (const layer of scene.layers) {
            warnings.push(...layerWindowWarnings(layer, scene.duration, `Layer "${layer.id}" in scene "${scene.id}"`, 'scene'));
        }
    }
    // An overlay's clock is the RENDERED video, which is shorter than the sum of scene durations
    // whenever transitions overlap — so the bound has to come from the frame layout, never from
    // adding up `scene.duration`, or this lint would promise a second the video does not have.
    // A document with no scenes renders nothing at all; its overlays have a bigger problem than
    // their windows, and warning on each of them would just bury it.
    const videoSeconds = documentDurationSeconds(doc);
    if (videoSeconds > 0) {
        for (const overlay of doc.overlays ?? []) {
            warnings.push(...layerWindowWarnings(overlay, videoSeconds, `Overlay "${overlay.id}"`, 'video'));
        }
    }
    return warnings;
}
/**
 * Every deterministic lint a document can trip, in one call — the list the write tools hand back to
 * the agent. One entry point on purpose: a new lint reaches every write surface by being added
 * HERE, instead of by being remembered at each call site.
 */
function documentWarnings(doc) {
    return [...documentTransitionWarnings(doc), ...documentTimingWarnings(doc), ...(0, caption_layout_1.documentCaptionWarnings)(doc)];
}
